import React, { useState, useEffect, useRef } from 'react';
import language from '../../../utils/LanguageSettings';
import RenderSnA from '../../functions/renderSnA';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import { fetchAdvertiser } from '../../functions/fetchAdvertiser';
import { SOURCE_URL } from '../../../config';
import { useInView } from 'react-intersection-observer';
import 'animate.css';
import { animationClass, animationStyle } from '../../../utils/FadeInEffect';
import AdvertiserTextLayout from './AdvertiserTextLayout';
import AdvertiserButtonLayout from './AdvertiserButtonLayout';
import translations from '../../../text/get-started-translations.json';
import closeImage from '../../../images/icon/icon-close.svg';
import { useAppDispatch, useAppSelector } from '../../../stores/hook';
import ImageFallback from '../../../utils/ImageFallback';
import { setPackInfoDetailStatus } from '../GetStarted/GetStartedSlice';
import "../../../css/advertiser-modal.css"

interface AdvertiserProps {

}

const Advertiser: React.FC<AdvertiserProps> = () => {
    const { ref: ref1, inView: inView1 } = useInView({
        threshold: 0.01,
        triggerOnce: true,
    });
    const { ref: ref2, inView: inView2 } = useInView({
        threshold: 0.01,
        triggerOnce: true,
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [content, setContent] = useState<any>(null);
    const [columnedContent, setColumnedContent] = useState<any[]>([]);
    const [seo, setSeo] = useState<any>(null);
    const [ga, setGa] = useState<any>(null);
    const pageTranslations = translations[language];
    const dispatch = useAppDispatch();
    const isShowDetailModal = useAppSelector((state) => state.getStarted.isShowPackInfoDetail);
    const listenerRef = useRef(null);
    const [isShow, setIsShow] = useState<boolean>(false);
    const [activeChannelId, setActiveChannelId] = useState<string>('');
    const [activeChannel, setActiveChannel] = useState<any>(null);
    const [activeCategory, setActiveCategory] = useState<string>('');
    const [isClosing, setIsClosing] = useState<boolean>(false);
    const columnedContentItems: React.SetStateAction<any[]> = [];

    useEffect(() => {
        const fetchData = async () => {
            let data = await fetchAdvertiser(language);
            if(data){
                setContent(data?.attributes);
                setLoading(false);
                setGa(data?.attributes?.GA || null);
                setSeo(data?.attributes?.SEO || null);
            }
        };
        fetchData();
    }, [language]);

    useEffect(()=>{
        handleColumnedContent(content?.ColumnedContent);
    },[content,language]);

    const handleColumnedContent = (items: any[]) => {
        setColumnedContent([]);
        items?.forEach((element: {__component: string}, index: number) => {
            switch(element?.__component){
                case "advertiser.button-layout":
                    columnedContentItems.push(<AdvertiserButtonLayout element={element} key={index}/>);
                    break;
                case "advertiser.text-layout":
                    columnedContentItems.push(<AdvertiserTextLayout element={element} key={index}/>);
                    break;
            }
        });
        setColumnedContent(columnedContentItems);
    }

    const gotoFormSection = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const targetLink = e.currentTarget;
        const targetId = targetLink.getAttribute('href')?.replace('#', '') || '';
        if (!targetId) {
            return;
        }
        
        const targetElement = document.getElementById(targetId);
        if (!targetElement) {
            return;
        }

        const navElement = document.querySelector('nav');
        const navHeight = navElement ? navElement.offsetHeight : 0;
        const targetTop = targetElement.getBoundingClientRect().top + window.pageYOffset;

        window.scrollTo({
            top: targetTop - navHeight,
            behavior: 'smooth'
        });
    }

    useEffect(() => {
        if (isShowDetailModal) {
            setTimeout(() => {
                setIsShow(true);
            }, 0);
            document.getElementsByTagName("body")[0].classList.add("modal-open");
        }
    }, [isShowDetailModal]);

    useEffect(() => {
        const handleLiener = (event: any) => {
            const targetDiv = document.getElementsByClassName('advertiser-channels')[0];
            if (targetDiv && !targetDiv.contains(event.target)) {
                handleClick();
            }
        };

        listenerRef.current = handleLiener;

        if (isShow) {
        document.addEventListener('click', handleLiener);
        } else {
        document.removeEventListener('click', handleLiener);
        }

        return () => {
        document.removeEventListener('click', listenerRef.current);
        };
    }, [isShow]);

    const handleClick = () => {
        setIsShow(false);
        setActiveChannel(null);
        setActiveChannelId("");
        dispatch(setPackInfoDetailStatus(false));
        document.getElementsByTagName("body")[0].classList.remove("modal-open");
    }

    const clickChannelItem = (channelId: string, channel: any, category: string) => {
        if (channelId === activeChannelId) {
            setIsClosing(true);
            setTimeout(() => {
                setActiveChannelId('');
                setActiveChannel(null);
                setActiveCategory('');
                setIsClosing(false);
            }, 500);
            return;
        }

        setIsClosing(true);
        setTimeout(() => {
            setActiveChannelId('');
            setActiveChannel(null);
            setActiveCategory('');
            setIsClosing(false);
            
            const timer = setTimeout(() => {
                setActiveChannelId(channelId);
                setActiveChannel(channel);
                setActiveCategory(category);
                clearTimeout(timer);
            }, 50);
        }, 500);
    };


    if (loading) {
        return (<>
            <div id="loader">
                <div className="spinner-border"></div>
            </div>
        </>
        );
    }

    const bannerImage = content?.Banner?.DesktopBannerImage?.data?.attributes?.url;
    const bannerImageTab = content?.Banner?.TabletBannerImage?.data?.attributes?.url ?? bannerImage;
    const bannerImageMob = content?.Banner?.MobileBannerImage?.data?.attributes?.url ?? bannerImageTab;

    const bannerImagePosition = content?.Banner?.BannerImagePosition?.toLowerCase();
    const bannerImagePositionTab = content?.Banner?.BannerImagePositionTab?.toLowerCase() ?? bannerImagePosition;
    const bannerImagePositionMob = content?.Banner?.BannerImagePositionMob?.toLowerCase() ?? bannerImagePositionTab;
    return (
      <>
        <RenderSnA seo={seo} ga={ga} />
        <section id="single-banner" style={{height: "fit-content"}} className="advertiser-content object-item" data-imgposition={bannerImagePosition} data-imgpositiontab={bannerImagePositionTab} data-imgpositionmob={bannerImagePositionMob}>
            <img src={SOURCE_URL + bannerImage} className='object-desktop-image w-100 main-img img-fit d-md-block d-none' style={{ objectPosition: bannerImagePosition }}/>
		    <img src={SOURCE_URL + bannerImageTab} className='object-tab-image w-100 main-img img-fit d-xl-none d-lg-block d-none' style={{ objectPosition: bannerImagePositionTab }}/>
            <img src={SOURCE_URL + bannerImageMob} className='object-mobile-image w-100 main-img img-fit d-md-none d-block' style={{ objectPosition: bannerImagePositionMob }}/>
            {content?.Banner?.BannerText && 
            <div className={`caption-text py-5 pxc ${content?.Banner?.BannerTextPosition?.toLowerCase()} ${content?.Banner?.TextColor?.toLowerCase()}`}>
                <div>
                    <div className='to-be-calculated'>
                        <div className='banner-text-field mb-1'>
                            <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.Banner?.BannerText}</ReactMarkdown>
                        </div>
                        <div className='clearfix'></div>
                        {content?.Banner?.BannerButtonLink?.URL && 
                        <a onClick={gotoFormSection} href={content?.Banner?.BannerButtonLink?.URL} target={content?.Banner?.BannerButtonLink?.OpenLinkInANewTab ? '_blank' : '_self'} className='default-btn mt-4'>{content?.Banner?.BannerButtonLink?.LinkText}</a>}
                    </div>
                </div>
            </div>}
        </section>
        <section id="introduction-text" className="light-gray-bg">
            <div ref={ref1} className={`container spacer introduction-text advertiser-content ${animationClass(inView1, 'up')}`}  style={animationStyle(inView1,'up')}>
                <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.Introduction}</ReactMarkdown>
            </div>
        </section>
        <section id="main-content">
            <div className="container spacer advertiser-content">
                <h3 className="mb-4 text-center font-medium pb-1">{content?.ColumnedContentTitle}</h3>
                {columnedContent && 
                <div className="negative-margin row justify-content-center small-margin">
                    {columnedContent}
                </div>}
            </div>
        </section>
        <section id="add-bottom-part" className="light-gray-bg"> 
            <div ref={ref2} className={`container text-center spacer advertiser-content ${animationClass(inView2, 'lightSpeedIn')}`}  style={animationStyle(inView2,'lightSpeedIn')}>
                <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>{content?.BottomContent}</ReactMarkdown>
                {content?.ContactNo &&
                <a href={`tel:${content?.ContactNo}`}>{content?.ContactNo}</a>}
                <a href={`mailto:${content?.EmailAddress}`}>{content?.EmailAddress}</a>
            </div>
        </section>
        <div className={`dvertiser-modal modal package-more-details ${isShow ? "show" : ""}`} id='popup-channels'style={{display: 'flex',visibility: isShow ? 'visible' : 'hidden',}}>
            <div className='modal-dialog modal-lg modal-dialog-centered'>
                <div className="modal-content">
                    <button className="close text-right" onClick={() => {handleClick()}} data-dismiss="modal" aria-label="Close">
                        <span>{pageTranslations.Close}</span>
                        <img src={closeImage} alt="Close icon" className='lazyloaded' />
                    </button>
                    <div className='pb-4 advertiser-channels'>
                        <h3 className='my-5 text-center font-medium mb-4 pb-1'>{content?.AdvertisementChannelsTitle}</h3>
                        {Array.isArray(content?.AdvertisementChannels) && content?.AdvertisementChannels?.map((item: any, index: number)=>{
                            const currentCategory = item?.Category;
                            const isCategoryActive = activeCategory === currentCategory && activeChannelId !== '';
                            return (
                            <div key={`category-${index}`}>
                                <div className="featured-logo-label py-3 text-sm-left plc advertiser-channel-title">{item?.Category}</div>
                                <div className='py-4 pxc channel-roww text-md-left channel-row'>
                                    <div className='row'>
                                        {Array.isArray(item?.channels?.data) && item?.channels?.data?.map((channel: any, k :number) => {
                                            const channelId = `${item?.Category}-${channel?.attributes?.number}`;
                                            const isChannelActive = activeChannelId === channelId;
                                            return (
                                            <div className="col-lg-2 col-md-4 col-sm-6" key={k}>
                                                <div className={`channel_item w-100 py-3 mb-3 ${isChannelActive ? "active" : ""}`} onClick={()=>clickChannelItem(channelId, channel?.attributes, currentCategory)}>
                                                    <ImageFallback
                                                        primarySrc={channel?.attributes?.icon?.data?.attributes?.url ? `${SOURCE_URL}` +channel?.attributes?.icon?.data?.attributes?.url : ''}
                                                        alt={`${channel?.attributes?.name} Channel`}
                                                        className='img-fluid lazyloaded'
                                                    />
                                                </div>
                                            </div>
                                        )})}
                                    </div>
                                </div>
                                <div className={`channel_description pl-sm-5 pl-0 pr-xl-0 pr-sm-3 pr-0 ${isCategoryActive ? "active" : ""} ${isClosing ? "closing" : ""}`} key={`desc-${index}`}> 
                                    {activeChannel && activeChannelId.indexOf(item?.Category) > -1 && 
                                    <div className="row py-sm-5 py-4 plc no-gutters pr-4">
                                        <div className="col-lg-4">
                                            <h5 className="mb-3 font-medium"><span className="light-gray">CH.{activeChannel?.number} </span>{activeChannel?.name}</h5>
                                        </div>
                                        <div className="col-lg-8">
                                            <p className="barlow-font">{activeChannel?.description}</p>
                                        </div>
                                    </div>}
                                </div>
                            </div>
                        )})}
                    </div>
                </div>
            </div>
        </div>
      </>
    );
};

export default Advertiser;